import { TFunction, withTranslation } from 'react-i18next';
import React, { useContext, useState } from 'react';
import { KibDisplayObserver } from '@chewy/kib-display-react';
import { PetInterface } from 'types/pet';
import { Section } from '../../types/puppyGuide';
import AnalyticsContext from '../../context/AnalyticsContext';
import {
  ANALYTICS_ACTION_IMPRESSION,
  ANALYTICS_LABEL_SUFFIX_HEADER,
  ANALYTICS_LABEL_SUFFIX_LEARN_MORE,
} from '../../AnalyticsProvider';
import { usePuppyGuide } from './PuppyGuideContext';
import IntroductionSection from './IntroductionSection';
import Promo from './Promo';
import FAQStack from './FAQStack';
import { ArticlesLayout } from './ArticlesLayout';
import { navItemsHeight, headerDefaultHeight, headerMaxHeight } from './constants';
import { getArticles, getFAQ, getIntro } from './content/contentHelper';
import './sections-layout.scss';

export interface SectionsLayoutProps {
  t: TFunction<string>;
  petProfile?: PetInterface;
}

export const SectionsLayout = ({ t, petProfile }: SectionsLayoutProps) => {
  const {
    refSections,
    stickyHeaderTopOffset,
    headerStickyEndLocationRef,
    headerStickyStartLocationRef,
  } = usePuppyGuide();
  const analytics = useContext(AnalyticsContext);

  const [analyticsHistory, setAnalyticsHistory] = useState<String[]>([]);

  const recordAnalytics = (analyticsAction: string, analyticsLabel: string) => {
    setAnalyticsHistory((prevState) => {
      const newAnalytics = `${analyticsAction}${analyticsLabel}`;
      if (prevState.includes(newAnalytics)) {
        return prevState;
      } else {
        analytics.trackStarterGuide(analyticsAction, analyticsLabel);
        return [...prevState, newAnalytics];
      }
    });
  };

  const headerTopOffset =
    stickyHeaderTopOffset > headerMaxHeight ? headerDefaultHeight : stickyHeaderTopOffset;
  const topOffset = headerTopOffset + navItemsHeight;
  const petName = petProfile?.name ? petProfile?.name : 'your puppy';
  const sections = t<string, Section[]>('puppyGuide.sections', { returnObjects: true, petName });

  return (
    <div ref={headerStickyEndLocationRef}>
      {sections.map((item, index) => {
        const headerStickyStartLocationRefProp = index === 0 ? headerStickyStartLocationRef : null;
        const sectionNumber = index + 1;
        const faq = getFAQ(sectionNumber);
        const articles = getArticles(sectionNumber);
        return (
          <div key={index} className="starter-guide-section-layout">
            <KibDisplayObserver
              enter={() => {
                recordAnalytics(
                  ANALYTICS_ACTION_IMPRESSION,
                  `${item.title}${ANALYTICS_LABEL_SUFFIX_HEADER}`,
                );
              }}
            >
              <IntroductionSection
                backgroundColor={item.backgroundColor}
                sectionNumber={sectionNumber}
                title={item.title}
                content={getIntro(sectionNumber)}
                sectionHeader={item.sectionHeader}
                author={item.author}
                video={item.video}
                reducedMotionImage={item.images.heroReducedMotion}
                ref={refSections[index]}
                headerStickyStartLocationRef={headerStickyStartLocationRefProp}
                topOffset={topOffset}
              />
            </KibDisplayObserver>
            {faq ? (
              <FAQStack faq={faq} sectionNumber={sectionNumber} recordAnalytics={recordAnalytics} />
            ) : null}
            {item.branding ? (
              <KibDisplayObserver
                updateVisible={() => {
                  recordAnalytics(ANALYTICS_ACTION_IMPRESSION, item.branding?.analyticsLabel || '');
                }}
              >
                <Promo branding={item.branding} recordAnalytics={recordAnalytics} />
              </KibDisplayObserver>
            ) : null}
            <KibDisplayObserver
              updateVisible={() => {
                recordAnalytics(
                  ANALYTICS_ACTION_IMPRESSION,
                  `${item.title}${ANALYTICS_LABEL_SUFFIX_LEARN_MORE}`,
                );
              }}
            >
              <ArticlesLayout data={articles} recordAnalytics={recordAnalytics} />
            </KibDisplayObserver>
          </div>
        );
      })}
    </div>
  );
};

export default withTranslation()(SectionsLayout);
